package gr.tech.meetinggenie.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.meetinggenie.components.layouts.AuthPageLayout
import gr.tech.meetinggenie.components.widgets.loadingSpinner
import gr.tech.meetinggenie.infrastructure.api.fetchMeeting
import gr.tech.meetinggenie.infrastructure.api.fetchMeetingResponses
import gr.tech.meetinggenie.infrastructure.api.fetchMyMeetingResponse
import gr.tech.meetinggenie.infrastructure.api.saveMeetingResponse
import gr.tech.meetinggenie.infrastructure.api.submitMeetingResponse
import gr.tech.meetinggenie.infrastructure.session.UserSession
import gr.tech.meetinggenie.models.meeting.ActiveProjectDTO
import gr.tech.meetinggenie.models.meeting.ClosingSoonDTO
import gr.tech.meetinggenie.models.meeting.LossDTO
import gr.tech.meetinggenie.models.meeting.MeetingFormDataDTO
import gr.tech.meetinggenie.models.meeting.MeetingResponseDTO
import gr.tech.meetinggenie.models.meeting.ProjectUpdateDTO
import gr.tech.meetinggenie.models.meeting.WinDTO
import gr.tech.meetinggenie.models.user.USER_ROLES
import gr.tech.meetinggenie.sections.ActiveProjectsTableInput
import gr.tech.meetinggenie.sections.ClosingSoonTableInput
import gr.tech.meetinggenie.sections.LossesTableInput
import gr.tech.meetinggenie.sections.MeetingSection
import gr.tech.meetinggenie.sections.MeetingSidebar
import gr.tech.meetinggenie.sections.ProjectUpdatesTableInput
import gr.tech.meetinggenie.sections.PulseCheckInput
import gr.tech.meetinggenie.sections.ResponsesView
import gr.tech.meetinggenie.sections.WeeklySummaryInput
import gr.tech.meetinggenie.sections.WinsTableInput
import gr.tech.meetinggenie.styles.ButtonStyle
import gr.tech.meetinggenie.styles.ErrorPageStyle
import gr.tech.meetinggenie.styles.PageSubtitleStyle
import gr.tech.meetinggenie.styles.PageTitleStyle
import gr.tech.meetinggenie.styles.SaveStatusStyle
import gr.tech.meetinggenie.styles.SubSectionHeadingStyle
import gr.tech.meetinggenie.styles.SubmittedBadgeStyle
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Ensures each list-based section starts with one blank row so users
 * don't have to click "+ Add" before they can start filling in the form.
 * Singleton sections (weeklySummary, pulseCheck) always have a value already.
 */
private fun MeetingFormDataDTO.withDefaults() = copy(
    wins           = wins.ifEmpty { listOf(WinDTO()) },
    losses         = losses.ifEmpty { listOf(LossDTO()) },
    activeProjects = activeProjects.ifEmpty { listOf(ActiveProjectDTO()) },
    closingSoon    = closingSoon.ifEmpty { listOf(ClosingSoonDTO()) },
    projectUpdates = projectUpdates.ifEmpty { listOf(ProjectUpdateDTO()) },
)

@Page(routeOverride = "meeting/{id}")
@Composable
fun MeetingPage() {
    AuthPageLayout(
        title = "Meeting",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        MeetingPageScreen()
    }
}

@Composable
fun MeetingPageScreen() {
    val ctx   = rememberPageContext()
    val scope = rememberCoroutineScope()

    val meetingId = ctx.route.params["id"]?.toIntOrNull()

    var activeSection by remember { mutableStateOf(MeetingSection.RESPONSES) }
    var isLoading     by remember { mutableStateOf(true) }
    var errorText     by remember { mutableStateOf("") }
    var meetingTitle  by remember { mutableStateOf("") }

    val responses = remember { mutableStateListOf<MeetingResponseDTO>() }

    var myResponse by remember { mutableStateOf(MeetingResponseDTO()) }
    var formData   by remember { mutableStateOf(MeetingFormDataDTO().withDefaults()) }

    var saveStatus by remember { mutableStateOf("") }
    var isSaving   by remember { mutableStateOf(false) }

    fun handleFetching() {
        scope.launch {
            if (meetingId == null) {
                errorText = "Invalid meeting ID."
                isLoading = false
                return@launch
            }

            var meetingLoaded    = false
            var responsesLoaded  = false
            var myResponseLoaded = false
            fun checkDone() { if (meetingLoaded && responsesLoaded && myResponseLoaded) isLoading = false }

            fetchMeeting(
                meetingId = meetingId,
                onSuccess = { meeting -> meetingTitle = meeting.title; meetingLoaded = true; checkDone() },
                onError   = { meetingLoaded = true; checkDone() }
            )
            fetchMeetingResponses(
                meetingId = meetingId,
                onSuccess = { list -> responses.clear(); responses.addAll(list); responsesLoaded = true; checkDone() },
                onError   = { errorText = it; isLoading = false }
            )
            fetchMyMeetingResponse(
                meetingId = meetingId,
                onSuccess = { resp -> myResponse = resp; formData = resp.formData.withDefaults(); myResponseLoaded = true; checkDone() },
                onError   = { myResponseLoaded = true; checkDone() }
            )
        }
    }

    fun handleSave() {
        if (meetingId == null) return
        isSaving = true
        saveStatus = ""
        scope.launch {
            saveMeetingResponse(
                meetingId = meetingId,
                formData  = formData,
                onSuccess = { saved ->
                    myResponse = saved
                    formData   = saved.formData
                    saveStatus = "Saved!"
                    isSaving   = false
                },
                onError = { msg -> saveStatus = "Error: $msg"; isSaving = false }
            )
        }
    }

    fun handleSubmit() {
        if (meetingId == null) return
        scope.launch {
            saveMeetingResponse(
                meetingId = meetingId,
                formData  = formData,
                onSuccess = {
                    scope.launch {
                        submitMeetingResponse(
                            meetingId = meetingId,
                            onSuccess = { myResponse = myResponse.copy(submitted = true); saveStatus = "Submitted!" },
                            onError   = { msg -> saveStatus = "Submit error: $msg" }
                        )
                    }
                },
                onError = { msg -> saveStatus = "Save error: $msg" }
            )
        }
    }

    LaunchedEffect(meetingId) {
        handleFetching()
    }


    Row(modifier = Modifier.fillMaxSize()) {
        MeetingSidebar(
            activeSection   = activeSection,
            isLoading = isLoading,
            submittedForm = myResponse.submitted,
            onSectionChange = { activeSection = it }
        )

        Column(
            modifier = Modifier.fillMaxSize().overflow(Overflow.Auto),
            horizontalAlignment = Alignment.Start
        ) {
            if (isLoading) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    loadingSpinner(iconSize = IconSize.X3)
                }
                return@Column
            }

            if (errorText.isNotEmpty()) {
                SpanText(errorText, modifier = ErrorPageStyle.toModifier())
                return@Column
            }

            Column(modifier = Modifier.fillMaxWidth().padding(leftRight = 24.px, top = 24.px, bottom = 8.px)) {
                H1(PageTitleStyle.toModifier().toAttrs()) { Text(meetingTitle.ifEmpty { "Meeting" }) }
                Row(Modifier.gap(8.px)) {
                    H3(PageSubtitleStyle.toModifier().toAttrs()) { Text(UserSession.info.value?.name ?: "") }
                    val submitted = myResponse.submitted
                    Span(attrs = SubmittedBadgeStyle.toModifier().color(if (submitted) Colors.Green else Colors.DarkRed).toAttrs()) {
                        Text(if (submitted) "● Submitted" else "● Not Submitted")
                    }
                }
                HorizontalDivider(Modifier.margin(top = 16.px))
            }

            when (activeSection) {
                MeetingSection.RESPONSES -> {
                    handleFetching()
                    ResponsesView(responses = responses)
                }

                MeetingSection.FORM -> {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(24.px),
                        horizontalAlignment = Alignment.Start
                    ) {
                        // ── Section 1 & 2: Wins ──────────────────────────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Wins This Week") }

                        WinsTableInput(
                            wins = formData.wins,
                            onUpdate = { updated -> formData = formData.copy(wins = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // ── Section 3: Losses / Delayed Deals ────────────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Losses / Delayed Deals") }

                        LossesTableInput(
                            losses = formData.losses,
                            onUpdate = { updated -> formData = formData.copy(losses = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // ── Section 4: Active Projects ────────────────────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Active Projects You're Working On") }

                        ActiveProjectsTableInput(
                            activeProjects = formData.activeProjects,
                            onUpdate = { updated -> formData = formData.copy(activeProjects = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // ── Section 5: Projects Hoping to Close Soon ──────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Projects Hoping to Close Soon") }

                        ClosingSoonTableInput(
                            closingSoon = formData.closingSoon,
                            onUpdate = { updated -> formData = formData.copy(closingSoon = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // ── Section 6: Project Updates ────────────────────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Project Updates (Only If Something Changed)") }

                        ProjectUpdatesTableInput(
                            projectUpdates = formData.projectUpdates,
                            onUpdate = { updated -> formData = formData.copy(projectUpdates = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // ── Section 7: Weekly Summary ─────────────────────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Weekly Summary (TL;DR)") }

                        WeeklySummaryInput(
                            weeklySummary = formData.weeklySummary,
                            onUpdate = { updated -> formData = formData.copy(weeklySummary = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // ── Section 8: Pulse Check ────────────────────────
                        H3(SubSectionHeadingStyle.toModifier().toAttrs()) { Text("Pulse Check") }

                        PulseCheckInput(
                            pulseCheck = formData.pulseCheck,
                            onUpdate = { updated -> formData = formData.copy(pulseCheck = updated); saveStatus = "" }
                        )

                        HorizontalDivider(Modifier.margin(topBottom = 16.px))

                        // Save / Submit
                        Row(horizontalArrangement = Arrangement.spacedBy(8.px)) {
                            if (!myResponse.submitted) {
                                Button(
                                    attrs = ButtonStyle.toModifier()
                                        .onClick { handleSubmit() }.toAttrs()
                                ) { Text("Submit") }
                            } else {
                                Button(
                                    attrs = ButtonStyle.toModifier()
                                        .onClick { handleSave() }.toAttrs()
                                ) { Text(if (isSaving) "Saving..." else "Save") }
                            }
                        }

                        if (saveStatus.isNotEmpty()) {
                            SpanText(
                                text = saveStatus,
                                modifier = SaveStatusStyle.toModifier()
                                    .color( color =
                                        if (saveStatus.startsWith("Error") || saveStatus.startsWith("Save error") || saveStatus.startsWith("Submit error"))
                                            Colors.Red
                                        else
                                            Colors.Green)
                            )
                        }
                    }
                }
            }
        }
    }
}
